import { Link, useParams } from 'react-router-dom'

// Demo published posts — in production, fetch from GET /api/public/blog
const DEMO_POSTS = [
  {
    post_id: 'post_001',
    slug: 'what-is-ensemble-pre-read',
    kind: 'methodology',
    title: 'What is an Ensemble Pre-Read?',
    subtitle: 'How 7 specialized reasoning passes replace 200 survey respondents for directional signal',
    industry: null,
    author_name: 'Echosome Team',
    published_at: Date.now() - 14 * 24 * 60 * 60 * 1000,
    status: 'published',
  },
  {
    post_id: 'post_002',
    slug: 'social-desirability-bias-churn',
    kind: 'post',
    title: 'Social Desirability Bias in Churn Research',
    subtitle: 'Why "too expensive" is the most expensive phrase in your exit survey',
    industry: 'saas',
    author_name: 'Echosome Team',
    published_at: Date.now() - 7 * 24 * 60 * 60 * 1000,
    status: 'published',
  },
]

function PostCard({ post }: { post: typeof DEMO_POSTS[0] }) {
  const kindLabel = post.kind === 'case_study' ? 'Case study' : post.kind === 'methodology' ? 'Methodology' : 'Blog'
  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <div className="bg-paper border border-border-gray rounded-2xl p-6 hover:border-echo-indigo/30 hover:shadow-sm transition-all">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-echo-indigo font-medium bg-echo-indigo/10 px-2 py-0.5 rounded-full">{kindLabel}</span>
          {post.industry && (
            <span className="text-xs text-slate-400 capitalize">{post.industry.replace('_', ' ')}</span>
          )}
        </div>
        <h2 className="text-base font-semibold text-deep-ink mb-1 group-hover:text-echo-indigo transition-colors">{post.title}</h2>
        <p className="text-sm text-slate-500 mb-3">{post.subtitle}</p>
        <div className="text-xs text-slate-400">
          {post.author_name} · {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
    </Link>
  )
}

export function BlogPage() {
  return (
    <div className="min-h-screen bg-off-white">
      <header className="border-b border-border-gray bg-paper px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-base font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>echosome</Link>
        <Link to="/signup" className="text-sm bg-echo-indigo text-white px-4 py-2 rounded-lg hover:bg-echo-indigo/90 transition-colors">
          Try free →
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-deep-ink mb-3" style={{ letterSpacing: '-0.03em' }}>Blog</h1>
          <p className="text-base text-slate-600">Methodology, case studies, and research insights from the Echosome team.</p>
        </div>

        <div className="space-y-4">
          {DEMO_POSTS.filter(p => p.status === 'published').map(post => (
            <PostCard key={post.post_id} post={post} />
          ))}
        </div>

        <div className="mt-8 pt-8 border-t border-border-gray">
          <Link to="/research" className="text-sm text-echo-indigo hover:underline">
            → See research reports & lead magnets
          </Link>
        </div>
      </main>
    </div>
  )
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = DEMO_POSTS.find(p => p.slug === slug)

  if (!post) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-deep-ink font-medium mb-2">Post not found</p>
          <Link to="/blog" className="text-sm text-echo-indigo underline">← Back to blog</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-off-white">
      <header className="border-b border-border-gray bg-paper px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-base font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>echosome</Link>
        <div className="flex items-center gap-3">
          <Link to="/blog" className="text-sm text-slate-500 hover:text-deep-ink">← Blog</Link>
          <Link to="/signup" className="text-sm bg-echo-indigo text-white px-4 py-2 rounded-lg hover:bg-echo-indigo/90 transition-colors">
            Try free →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-echo-indigo font-medium bg-echo-indigo/10 px-2 py-0.5 rounded-full capitalize">{post.kind}</span>
          </div>
          <h1 className="text-2xl font-bold text-deep-ink mb-2" style={{ letterSpacing: '-0.02em' }}>{post.title}</h1>
          <p className="text-base text-slate-500 mb-4">{post.subtitle}</p>
          <p className="text-sm text-slate-400">
            {post.author_name} · {new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="bg-paper border border-border-gray rounded-2xl p-6">
          <p className="text-sm text-slate-500 italic">Full post content rendered here from body_md field in production.</p>
        </div>

        <div className="mt-8 bg-echo-indigo rounded-2xl p-6 text-white text-center">
          <p className="text-base font-medium mb-2">Try Echosome on your own data</p>
          <Link to="/signup" className="inline-block bg-white text-echo-indigo px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors">
            Get started free →
          </Link>
        </div>
      </main>
    </div>
  )
}
